import { useEffect, useState, useCallback } from "react";
import { Hammer, ShieldCheck, Clock } from "lucide-react";
import { portalApi } from "../../lib/portalApi";

const SCOPE_BADGE = {
  active: { l: "Active", cls: "bg-[#3F6E2C]/15 text-[#3F6E2C] border-[#3F6E2C]/30" },
  deferred: { l: "Deferred", cls: "bg-[#D4A963]/20 text-[#A6822F] border-[#D4A963]/50" },
  excluded_superseded: { l: "Excluded · superseded", cls: "bg-[#A3141A]/10 text-[#A3141A] border-[#A3141A]/30" },
  evidence: { l: "Evidence", cls: "bg-[#7A6B5F]/15 text-[#7A6B5F] border-[#7A6B5F]/30" },
};

function GateFlag({ label, pending }) {
  return (
    <span className={`text-[9px] tracking-[0.14em] uppercase px-2 py-1 border ${
      pending ? "bg-[#D4A963]/15 text-[#A6822F] border-[#D4A963]/40" : "bg-[#3F6E2C]/15 text-[#3F6E2C] border-[#3F6E2C]/30"
    }`}>
      {label}: {pending ? "Pending" : "Received"}
    </span>
  );
}

function GroupBlock({ slug, title, group, canEdit, onChanged }) {
  const measured = group.measured || {};
  return (
    <div className="border border-[#6E1F2C]/15 bg-white p-4">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#6E1F2C] font-medium">{title}</p>
        <span className="text-[11px] text-[#3E2A22] tabular-nums" data-testid={`demolition-measured-${title.toLowerCase()}`}>
          {measured.percent != null
            ? `Measured ${measured.percent}%`
            : "Measured: unresolved — approved weights pending"}
        </span>
      </div>
      {group.note && <p className="text-[11px] text-[#3E2A22] mb-3 whitespace-pre-line">{group.note}</p>}
      <div className="space-y-1.5">
        {group.tasks.map((t) => {
          const b = SCOPE_BADGE[t.scope_status || "active"] || SCOPE_BADGE.active;
          return (
            <div key={t.id} className="flex items-start justify-between gap-2 border-b border-[#6E1F2C]/8 pb-1.5" data-testid={`demolition-task-${t.id}`}>
              <div className="min-w-0">
                <p className="text-[12px] text-[#1C110C] leading-snug">{t.name}</p>
                {t.scope_note && <p className="text-[10px] text-[#7A6B5F] leading-snug">{t.scope_note}</p>}
              </div>
              <div className="shrink-0 flex items-center gap-1.5">
                <span className="text-[11px] tabular-nums text-[#3E2A22]">{t.progress_percent || 0}%</span>
                <span className={`text-[9px] tracking-[0.12em] uppercase px-1.5 py-0.5 border ${b.cls}`}>{b.l}</span>
                {canEdit && (t.scope_status || "active") === "active" && (
                  <WeightEditor slug={slug} task={t} onChanged={onChanged} />
                )}
              </div>
            </div>
          );
        })}
        {group.tasks.length === 0 && <p className="text-[11px] italic text-[#7A6B5F]">No rows in this group.</p>}
      </div>
    </div>
  );
}

function WeightEditor({ slug, task, onChanged }) {
  const [val, setVal] = useState(task.approved_weight ?? "");
  const save = async () => {
    const num = val === "" ? null : Number(val);
    if (num !== null && (!Number.isFinite(num) || num <= 0)) return;
    try {
      await portalApi.setDemolitionTaskScope(slug, task.id,
        num === null ? { clear_approved_weight: true } : { approved_weight: num });
      onChanged && onChanged();
    } catch (e) { window.alert(e.message); }
  };
  return (
    <input
      type="number" min="0" step="0.1" value={val}
      onChange={(e) => setVal(e.target.value)} onBlur={save}
      placeholder="wt"
      title="Approved quantity/value weight (blank = unresolved)"
      data-testid={`demolition-weight-${task.id}`}
      className="w-14 bg-[#FAF5EC] border border-[#6E1F2C]/20 px-1 py-0.5 text-[10px] text-[#1C110C]"
    />
  );
}

export default function DemolitionScopeCard({ slug, isAdmin, canEdit }) {
  const [data, setData] = useState(null);
  const [fixPlan, setFixPlan] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    portalApi.getDemolitionView(slug).then(setData).catch(() => setData(null));
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  if (!data.has_scope) {
    if (!isAdmin || slug !== "rosvilla-murooj-villa-572") return null;
    return (
      <div className="bg-[#FAF5EC] border border-[#D4A963]/50 p-4 mt-4" data-testid="demolition-scope-setup">
        <p className="text-[11px] text-[#3E2A22] mb-2">
          Demolition scope view is not configured. Preview the guarded classification (no writes), then apply once.
        </p>
        <div className="flex gap-2 flex-wrap">
          <button type="button" disabled={busy} data-testid="demolition-fix-dryrun"
            onClick={async () => { setBusy(true); try { setFixPlan(await portalApi.muroojDemolitionScopeFix(slug, true)); } catch (e) { window.alert(e.message); } finally { setBusy(false); } }}
            className="border border-[#6E1F2C] text-[#6E1F2C] px-3 py-2 text-[10px] tracking-[0.22em] uppercase hover:bg-[#6E1F2C] hover:text-white">
            Preview classification (dry-run)
          </button>
          {fixPlan && (
            <button type="button" disabled={busy} data-testid="demolition-fix-apply"
              onClick={async () => {
                if (!window.confirm("Apply guarded demolition-scope classification? No task % is changed; already-edited rows are preserved.")) return;
                setBusy(true);
                try { await portalApi.muroojDemolitionScopeFix(slug, false); setFixPlan(null); load(); }
                catch (e) { window.alert(e.message); } finally { setBusy(false); }
              }}
              className="bg-[#6E1F2C] text-[#FAF5EC] px-3 py-2 text-[10px] tracking-[0.22em] uppercase hover:bg-[#56151F]">
              Apply once ({fixPlan.classified?.length ?? 0} rows, {fixPlan.status_heals?.length ?? 0} status heals)
            </button>
          )}
        </div>
        {fixPlan && (
          <div className="mt-2 max-h-40 overflow-y-auto text-[10px] text-[#3E2A22]" data-testid="demolition-fix-plan">
            {(fixPlan.classified || []).map((c) => (
              <p key={c.id}>{c.name} → {c.demolition_group} / {c.scope_status}</p>
            ))}
          </div>
        )}
      </div>
    );
  }

  const gate = data.gate || {};
  const components = data.owner_components || [];
  const editable = canEdit && data.viewer_can_edit;
  return (
    <section className="mt-4 space-y-3" data-testid="demolition-scope-card">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] inline-flex items-center gap-1.5">
            <Hammer size={11} /> Demolition — current scope
          </p>
          <p className="text-[11px] text-[#7A6B5F]">
            Two groups (indoor / outdoor). Legacy rows retained as evidence; deferred and superseded rows
            are excluded from the current denominator — never deleted. Overall programme % is separate and unaffected.
          </p>
        </div>
        {components.length > 0 && (
          <div className="bg-[#1C110C] text-[#FAF5EC] px-3 py-2 max-w-md" data-testid="demolition-owner-components">
            <p className="text-[9px] tracking-[0.24em] uppercase text-[#D4A963] mb-1">Owner estimates — by component (no certified aggregate)</p>
            {components.map((c) => (
              <p key={c.label} className="text-[10px] leading-snug"><strong>{c.label}:</strong> {c.value}</p>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <GroupBlock slug={slug} title="Indoor" group={data.groups.indoor} canEdit={editable} onChanged={load} />
        <GroupBlock slug={slug} title="Outdoor" group={data.groups.outdoor} canEdit={editable} onChanged={load} />
      </div>

      <div className="bg-white border border-[#D4A963]/40 p-4" data-testid="demolition-gate-card">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#A6822F] inline-flex items-center gap-1.5">
            <ShieldCheck size={11} /> 50% demolition payment-readiness gate
          </p>
          <span className={`text-[10px] tracking-[0.18em] uppercase px-2.5 py-1 ${
            gate.measured_reached ? "bg-[#3F6E2C] text-white" : "bg-[#7A6B5F] text-white"
          }`} data-testid="demolition-gate-status">
            {gate.measured_reached ? "Reached — measured" : "Pending verified demolition aggregate"}
          </span>
        </div>
        <p className="text-[11px] text-[#3E2A22] mb-2" data-testid="demolition-measured-overall">
          Measured demolition progress:{" "}
          {data.measured_overall?.percent != null
            ? `${data.measured_overall.percent}%`
            : "unresolved — approved quantity/value weights missing"}
          {" · "}threshold {gate.threshold_percent}%
        </p>
        <div className="flex flex-wrap gap-1.5 mb-2">
          <GateFlag label="Certification" pending={gate.certification_pending} />
          <GateFlag label="WIR" pending={gate.wir_pending} />
          <GateFlag label="Joint measurement" pending={gate.joint_measurement_pending} />
          <GateFlag label="Disposal receipts" pending={gate.disposal_receipts_pending} />
        </div>
        <p className="text-[10px] text-[#7A6B5F] italic inline-flex items-start gap-1">
          <Clock size={10} className="mt-0.5 shrink-0" /> {gate.note}
        </p>
      </div>
    </section>
  );
}
